import React, { useState, useEffect } from 'react';
import { 
  Typography, 
  Box, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  DialogActions,
  Rating,
  Divider,
  List,
  ListItem,
  Chip,
  Grid,
  LinearProgress,
  Tabs,
  Tab,
  InputAdornment,
  TablePagination,
  Alert,
  CircularProgress,
  Avatar
} from '@mui/material';
import { 
    Search as SearchIcon,
    AssignmentInd as AssignedIcon,
    Public as DiscoveryIcon,
    CheckCircle as CheckCircleIcon,
    Warning as WarningIcon,
    Person as PersonIcon,
    School as SchoolIcon,
    MenuBook as CourseIcon,
    CalendarMonth as CalendarIcon
} from '@mui/icons-material';
import api from '../services/api';

const resolveFileUrl = (url?: string) => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  if (url.startsWith('/uploads/')) return `/api/v1${url}`;
  if (!url.startsWith('/')) return `/api/v1/uploads/${url}`;
  return url;
};

const SupervisorStudents = () => {
  const [tab, setTab] = useState(() => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    return user.role === 'CO_TEACHER' ? 2 : 0;
  });
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [settings, setSettings] = useState<any>(null);
  const [profile, setProfile] = useState<any>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);

  // Modal States
  const [openEval, setOpenEval] = useState(false);
  const [openConfirmEval, setOpenConfirmEval] = useState(false);
  const [submittingEval, setSubmittingEval] = useState(false);
  const [selectedPlacement, setSelectedPlacement] = useState<any>(null);
  const [evalRole, setEvalRole] = useState<'PROFESSIONAL' | 'SPECIALIST' | 'CO_TEACHER'>('PROFESSIONAL');
  const [activeForm, setActiveForm] = useState<any>(null);
  
  const [rubric, setRubric] = useState<any>({});
  const [feedback, setFeedback] = useState('');
  
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const setRes = await api.get('/settings');
      setSettings(setRes.data);

      if (user.role === 'SUPERVISOR') {
          const profRes = await api.get('/supervisors/me');
          setProfile(profRes.data);
      } else if (user.role === 'CO_TEACHER') {
          const profRes = await api.get('/co-teachers/me');
          setProfile(profRes.data);
      }

      if (tab === 0 && user.role === 'SUPERVISOR') {
          // Professional Round (Discovery) for Supervisor
          const res = await api.get(`/evaluations/discovery?search=${searchTerm}`);
          const validStudents = (res.data || []).filter((s: any) => s.requires_supervision === 1 || s.requires_supervision === true || s.requires_supervision === undefined);
          setStudents(validStudents);
      } else {
          // Specialist, Co-Teacher, or Admin view
          const res = await api.get(`/evaluations/assigned-students?search=${searchTerm}`);
          const validStudents = (res.data || []).filter((s: any) => s.requires_supervision === 1 || s.requires_supervision === true || s.requires_supervision === undefined);
          setStudents(validStudents);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    setPage(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, searchTerm]);

  const handleOpenEval = async (student: any, role: any) => {
    setLoading(true);
    try {
      const formRes = await api.get(`/evaluation-forms/active/${role}`);
      const form = formRes.data;
      setActiveForm(form);
      setSelectedPlacement(student);
      setEvalRole(role);
      
      const initialRubric: any = {};
      form.sections?.forEach((section: any) => {
        section.items?.forEach((item: any) => {
          initialRubric[item.id] = 4;
        });
      });
      setRubric(initialRubric);
      setFeedback('');
      setOpenEval(true);
    } catch (err: any) {
      alert(err.response?.data?.message || 'ไม่พบแบบประเมินที่เปิดใช้งานสำหรับรอบนี้');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenConfirmEval = () => {
    // Check if any items are unrated (0 or undefined)
    let unratedCount = 0;
    activeForm?.sections?.forEach((sec: any) => {
      sec.items?.forEach((item: any) => {
        if (rubric[item.id] === undefined || rubric[item.id] === 0) {
          unratedCount += 1;
        }
      });
    });
    if (unratedCount > 0) {
      if (!window.confirm(`มีข้อประเมินที่ยังไม่ได้ให้คะแนนหรือได้ 0 คะแนน จำนวน ${unratedCount} ข้อ คุณต้องการดำเนินการต่อไปเพื่อยืนยันคะแนนหรือไม่?`)) {
        return;
      }
    }
    setOpenConfirmEval(true);
  };

  const handleFinalSubmitEval = async () => {
    setSubmittingEval(true);
    try {
      await api.post('/evaluations', {
        placementId: selectedPlacement.placement_id,
        evaluator_role: evalRole,
        criteria_json: rubric,
        feedback,
        signature_data: 'Signed electronically'
      });
      setOpenConfirmEval(false);
      setOpenEval(false);
      fetchData();
      alert('บันทึกผลการประเมินสมรรถนะเรียบร้อยแล้ว');
    } catch (err: any) {
      alert(err.response?.data?.message || 'เกิดข้อผิดพลาดในการบันทึกผลการประเมิน');
    } finally {
      setSubmittingEval(false);
    }
  };

  const isProfessionalOpen = settings?.eval_professional_open === '1';
  const isSpecialistOpen = settings?.eval_specialist_open === '1';
  const isCoTeacherOpen = settings?.eval_coteacher_open === '1';
  const isAdminOrStaff = user.role === 'ADMIN' || user.role === 'STAFF';
  const isSupervisorOrCoTeacher = user.role === 'SUPERVISOR' || user.role === 'CO_TEACHER';
  const canViewScoreColumn = !isSupervisorOrCoTeacher;

  return (
    <Box sx={{ position: 'relative' }}>
      {loading && <LinearProgress sx={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 2000 }} />}
      
      <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <AssignedIcon color="primary" /> การนิเทศและประเมินผลสมรรถนะ
      </Typography>

      {user.role === 'CO_TEACHER' && profile?.school_name && (
        <Box sx={{ p: 2, mb: 3, bgcolor: '#f0fdf4', borderRadius: 2, border: '1px solid #bbf7d0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#166534' }}>
              🏫 โรงเรียนสังกัดครูพี่เลี้ยง: {profile.school_name}
            </Typography>
            <Typography variant="caption" sx={{ color: '#15803d' }}>
              ระบบแสดงเฉพาะรายชื่อนักศึกษาที่ฝึกปฏิบัติการสอนในโรงเรียนนี้ เพื่อความถูกต้องในการประเมิน
            </Typography>
          </Box>
          <Chip label="โรงเรียนเดียวกัน" color="success" size="small" variant="outlined" sx={{ fontWeight: 'bold' }} />
        </Box>
      )}

      <Paper sx={{ mb: 3 }}>
        <Tabs value={tab} onChange={(e, v) => setTab(v)} variant="fullWidth" indicatorColor="primary" textColor="primary">
          {(user.role === 'SUPERVISOR' || isAdminOrStaff) && <Tab value={0} icon={<DiscoveryIcon />} label="รอบวิชาชีพครู" />}
          {(user.role === 'SUPERVISOR' || isAdminOrStaff) && <Tab value={1} icon={<AssignedIcon />} label="รอบวิชาเอก" />}
          {(user.role === 'CO_TEACHER' || isAdminOrStaff) && <Tab value={2} icon={<AssignedIcon />} label="การประเมินจากครูพี่เลี้ยง" />}
        </Tabs>
      </Paper>

      <Paper sx={{ p: 2, mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="ค้นหาชื่อนักศึกษา หรือ โรงเรียน..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            slotProps={{
                input: {
                    startAdornment: (
                        <InputAdornment position="start">
                            <SearchIcon color="action" />
                        </InputAdornment>
                    ),
                },
            }}
          />
      </Paper>

      <Box sx={{ mb: 1, display: 'flex', justifyContent: 'flex-end' }}>
          <Typography variant="caption" color="textSecondary">
              จำนวนนักศึกษาทั้งหมดในรอบนี้: <b>{students.length}</b> ราย
          </Typography>
      </Box>

      <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: 2 }}>
        <Table sx={{ minWidth: 800 }}>
          <TableHead sx={{ bgcolor: 'grey.100' }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>นักศึกษา</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>สาขาวิชา</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>โรงเรียน</TableCell>
              {canViewScoreColumn && (
                <TableCell align="center" sx={{ fontWeight: 'bold' }}>คะแนนรอบนี้</TableCell>
              )}
              <TableCell align="center" sx={{ fontWeight: 'bold' }}>การจัดการ</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {students.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((s: any) => (
              <TableRow key={s.placement_id} hover>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Avatar 
                      src={s.student_image_url ? resolveFileUrl(s.student_image_url) : (s.image_url ? resolveFileUrl(s.image_url) : undefined)} 
                      sx={{ width: 36, height: 36, bgcolor: 'primary.light', fontSize: '0.9rem', fontWeight: 'bold' }}
                    >
                      {s.first_name ? s.first_name[0] : 'U'}
                    </Avatar>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{s.prefix}{s.first_name} {s.last_name}</Typography>
                      <Typography variant="caption" color="textSecondary">{s.student_code}</Typography>
                    </Box>
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{s.major || '-'}</Typography>
                </TableCell>
                <TableCell>{s.school_name}</TableCell>
                {canViewScoreColumn && (
                  <TableCell align="center">
                    {s.requires_supervision === 0 || s.requires_supervision === false ? (
                      <Typography variant="caption" color="textSecondary">ยกเว้นการนิเทศ</Typography>
                    ) : (
                      <Typography variant="body2" color="primary" sx={{ fontWeight: 'bold' }}>
                        {tab === 0 ? (s.professional_score ? `${parseFloat(s.professional_score).toFixed(0)}%` : '-') :
                         tab === 1 ? (s.specialist_score ? `${parseFloat(s.specialist_score).toFixed(0)}%` : '-') :
                         (s.coteacher_score ? `${parseFloat(s.coteacher_score).toFixed(0)}%` : '-')}
                      </Typography>
                    )}
                  </TableCell>
                )}
                <TableCell align="center">
                  {s.requires_supervision === 0 || s.requires_supervision === false ? (
                    <Chip
                      label="ไม่ต้องรับการนิเทศ"
                      size="small"
                      sx={{ bgcolor: '#F1F5F9', color: '#64748B', fontWeight: 600 }}
                    />
                  ) : ((tab === 0 && isProfessionalOpen) || (tab === 1 && isSpecialistOpen) || (tab === 2 && isCoTeacherOpen)) ? (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'center' }}>
                        {((tab === 0 && s.professional_score) || (tab === 1 && s.specialist_score) || (tab === 2 && s.coteacher_score)) ? (
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                <Chip label="ประเมินแล้ว" color="success" size="small" sx={{ mb: 0.5 }} />
                                {isAdminOrStaff && (
                                    <Button 
                                        size="small" 
                                        color="error" 
                                        variant="outlined" 
                                        onClick={() => {
                                            if (window.confirm('ยืนยันการล้างผลการประเมินเพื่อประเมินใหม่?')) {
                                                api.delete(`/evaluations/${s.placement_id}/${tab === 0 ? 'PROFESSIONAL' : (tab === 1 ? 'SPECIALIST' : 'CO_TEACHER')}`)
                                                    .then(() => fetchData())
                                                    .catch(err => alert('ไม่สามารถล้างข้อมูลได้'));
                                            }
                                        }}
                                    >
                                        แก้ไข (Reset)
                                    </Button>
                                )}
                            </Box>
                        ) : (
                            <Button 
                                variant="contained" 
                                size="small" 
                                onClick={() => handleOpenEval(s, tab === 0 ? 'PROFESSIONAL' : (tab === 1 ? 'SPECIALIST' : 'CO_TEACHER'))}
                            >
                                ประเมินผล
                            </Button>
                        )}
                      </Box>
                  ) : (
                      <Chip label="ปิดรับการประเมิน" size="small" disabled variant="outlined" />
                  )}
                </TableCell>
              </TableRow>
            ))}
            {students.length === 0 && (
              <TableRow>
                <TableCell colSpan={4 + (canViewScoreColumn ? 1 : 0)} align="center" sx={{ py: 4 }}>
                  <Typography color="textSecondary">ไม่พบข้อมูลนักศึกษา</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[25, 50, 100]}
          component="div"
          count={students.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="รายการต่อหน้า:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} จาก ${count}`}
        />
      </TableContainer>

      {/* Evaluation Dialog */}
      <Dialog open={openEval} onClose={() => setOpenEval(false)} fullWidth maxWidth="md">
        <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white' }}>
          แบบประเมินสมรรถนะนักศึกษา: {evalRole === 'PROFESSIONAL' ? 'ด้านวิชาชีพครู' : (evalRole === 'SPECIALIST' ? 'ด้านวิชาเอก' : 'ครูพี่เลี้ยง')}
        </DialogTitle>
        <DialogContent dividers sx={{ pt: 2.5 }}>
          {/* Enhanced Student Profile Card */}
          <Paper 
            elevation={0}
            sx={{ 
              p: 2.5, 
              mb: 3, 
              bgcolor: '#f8fafc', 
              borderRadius: 3, 
              border: '1px solid #e2e8f0',
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'center', sm: 'flex-start' },
              gap: 2.5
            }}
          >
            <Avatar 
              src={selectedPlacement?.student_image_url ? resolveFileUrl(selectedPlacement.student_image_url) : (selectedPlacement?.image_url ? resolveFileUrl(selectedPlacement.image_url) : undefined)} 
              alt={`${selectedPlacement?.first_name || ''}`}
              sx={{ 
                width: 72, 
                height: 72, 
                bgcolor: 'primary.main', 
                fontSize: '1.75rem', 
                fontWeight: 'bold',
                boxShadow: 2,
                border: '2px solid white'
              }}
            >
              {selectedPlacement?.first_name ? selectedPlacement.first_name[0] : 'U'}
            </Avatar>

            <Box sx={{ flexGrow: 1, width: '100%' }}>
              <Box sx={{ display: 'flex', justifyContent: { xs: 'center', sm: 'space-between' }, alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 800, color: 'text.primary', textAlign: { xs: 'center', sm: 'left' } }}>
                  {selectedPlacement?.prefix || ''}{selectedPlacement?.first_name} {selectedPlacement?.last_name}
                </Typography>
                <Chip 
                  label={evalRole === 'PROFESSIONAL' ? 'รอบด้านวิชาชีพครู' : (evalRole === 'SPECIALIST' ? 'รอบด้านวิชาเอก' : 'รอบครูพี่เลี้ยง')} 
                  color="primary" 
                  size="small" 
                  sx={{ fontWeight: 'bold' }}
                />
              </Box>

              <Grid container spacing={1.5}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="body2" sx={{ color: 'text.secondary', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <PersonIcon fontSize="small" sx={{ color: 'action.active', fontSize: '1.1rem' }} />
                    รหัสนักศึกษา: <strong style={{ color: '#1e293b' }}>{selectedPlacement?.student_code || '-'}</strong>
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="body2" sx={{ color: 'text.secondary', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    สาขาวิชา: <strong style={{ color: '#1e293b' }}>{selectedPlacement?.major || '-'}</strong>
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="body2" sx={{ color: 'text.secondary', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <SchoolIcon fontSize="small" sx={{ color: 'action.active', fontSize: '1.1rem' }} />
                    สถานศึกษาฝึกประสบการณ์: <strong style={{ color: '#1e293b' }}>{selectedPlacement?.school_name || '-'}</strong>
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="body2" sx={{ color: 'text.secondary', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <CourseIcon fontSize="small" sx={{ color: 'action.active', fontSize: '1.1rem' }} />
                    รายวิชาฝึกประสบการณ์: <strong style={{ color: '#1e293b' }}>{selectedPlacement?.course_code ? `${selectedPlacement.course_code} ${selectedPlacement.course_name || ''}` : '-'}</strong>
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="body2" sx={{ color: 'text.secondary', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <CalendarIcon fontSize="small" sx={{ color: 'action.active', fontSize: '1.1rem' }} />
                    ภาคเรียน/ปีการศึกษา: <strong style={{ color: '#1e293b' }}>{selectedPlacement?.semester ? `ภาคเรียนที่ ${selectedPlacement.semester}/${selectedPlacement.academic_year}` : '-'}</strong>
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          </Paper>
          
          {activeForm?.sections?.map((section: any, sIdx: number) => (
            <Box key={section.id} sx={{ mb: 4 }}>
              <Typography variant="subtitle1" sx={{ bgcolor: 'grey.100', p: 1, fontWeight: 'bold' }}>{section.title}</Typography>
              <Box sx={{ mt: 2, ml: 2 }}>
                  {section.items?.map((item: any) => (
                    <Box key={item.id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1, borderBottom: '1px solid #f0f0f0' }}>
                      <Typography variant="caption" sx={{ flexGrow: 1, pr: 2 }}>{item.label}</Typography>
                      <Rating value={rubric[item.id] || 0} max={item.max_score || 4} onChange={(e, v) => setRubric({...rubric, [item.id]: v || 0})} />
                    </Box>
                  ))}
              </Box>
            </Box>
          ))}

          <Divider sx={{ my: 3 }} />
          {(() => {
            const currentTotal = Object.values(rubric).reduce((s: number, v: any) => s + Number(v || 0), 0);
            let formMax = 0;
            activeForm?.sections?.forEach((sec: any) => {
              sec.items?.forEach((item: any) => {
                formMax += (item.max_score || 4);
              });
            });
            const displayMax = formMax > 0 ? formMax : 100;
            const percent = ((currentTotal / displayMax) * 100).toFixed(0);

            return (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, p: 1.5, bgcolor: '#f8fafc', borderRadius: 1.5 }}>
                 <Typography variant="h6" sx={{ fontWeight: 'bold' }}>คะแนนรวมเบื้องต้น:</Typography>
                 <Box sx={{ textAlign: 'right' }}>
                   <Typography variant="h4" color="primary" sx={{ fontWeight: 'bold' }}>
                     {currentTotal} <Typography component="span" variant="h6" color="textSecondary">/ {displayMax}</Typography>
                   </Typography>
                   <Typography variant="caption" color="textSecondary">({percent}%)</Typography>
                 </Box>
              </Box>
            );
          })()}
          <TextField fullWidth multiline rows={3} label="ข้อเสนอแนะ" value={feedback} onChange={e => setFeedback(e.target.value)} />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpenEval(false)} color="inherit">ยกเลิก</Button>
          <Button onClick={handleOpenConfirmEval} variant="contained" color="primary">ยืนยันการประเมิน</Button>
        </DialogActions>
      </Dialog>

      {/* Score Confirmation Dialog */}
      <Dialog 
        open={openConfirmEval} 
        onClose={() => !submittingEval && setOpenConfirmEval(false)} 
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: 'warning.main', color: 'white', display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningIcon /> ยืนยันผลคะแนนการประเมินสมรรถนะ
        </DialogTitle>
        <DialogContent dividers sx={{ pt: 2.5 }}>
          {/* Student Info Card */}
          <Paper variant="outlined" sx={{ p: 2, mb: 2.5, bgcolor: '#f8fafc', borderRadius: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <Avatar 
                src={selectedPlacement?.student_image_url ? resolveFileUrl(selectedPlacement.student_image_url) : (selectedPlacement?.image_url ? resolveFileUrl(selectedPlacement.image_url) : undefined)} 
                sx={{ width: 52, height: 52, bgcolor: 'primary.main', fontWeight: 'bold' }}
              >
                {selectedPlacement?.first_name ? selectedPlacement.first_name[0] : 'U'}
              </Avatar>
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                  {selectedPlacement?.prefix || ''}{selectedPlacement?.first_name} {selectedPlacement?.last_name}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  รหัส {selectedPlacement?.student_code || '-'} | สาขา {selectedPlacement?.major || '-'}
                </Typography>
              </Box>
            </Box>
            <Grid container spacing={1.5} sx={{ pl: { xs: 0, sm: 1 } }}>
              <Grid size={{ xs: 12 }}>
                <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>สถานศึกษาฝึกประสบการณ์:</Typography>
                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{selectedPlacement?.school_name || '-'}</Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>รายวิชาฝึกประสบการณ์:</Typography>
                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{selectedPlacement?.course_code ? `${selectedPlacement.course_code} ${selectedPlacement.course_name || ''}` : '-'}</Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>ภาคเรียน/ปีการศึกษา:</Typography>
                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{selectedPlacement?.semester ? `ภาคเรียนที่ ${selectedPlacement.semester}/${selectedPlacement.academic_year}` : '-'}</Typography>
              </Grid>
              <Grid size={{ xs: 12 }}>
                <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>รอบการประเมิน:</Typography>
                <Chip 
                  label={evalRole === 'PROFESSIONAL' ? 'รอบด้านวิชาชีพครู' : (evalRole === 'SPECIALIST' ? 'รอบด้านวิชาเอก' : 'รอบการประเมินจากครูพี่เลี้ยง')} 
                  size="small" 
                  color="primary" 
                  variant="outlined"
                  sx={{ fontWeight: 'bold', mt: 0.5 }}
                />
              </Grid>
            </Grid>
          </Paper>

          {/* Score Calculation Breakdown */}
          {(() => {
            let totalScore = 0;
            let maxScore = 0;
            const sectionScores: any[] = [];

            activeForm?.sections?.forEach((section: any) => {
              let secTotal = 0;
              let secMax = 0;
              section.items?.forEach((item: any) => {
                const itemScore = rubric[item.id] !== undefined ? Number(rubric[item.id]) : 0;
                secTotal += itemScore;
                secMax += (item.max_score || 4);
              });
              totalScore += secTotal;
              maxScore += secMax;
              sectionScores.push({
                title: section.title,
                score: secTotal,
                max: secMax
              });
            });

            const percent = maxScore > 0 ? ((totalScore / maxScore) * 100).toFixed(0) : '0';

            return (
              <Box>
                {/* Total Score Display */}
                <Paper 
                  sx={{ 
                    p: 2.5, 
                    mb: 2.5, 
                    textAlign: 'center', 
                    bgcolor: '#f0fdf4', 
                    border: '1px solid #bbf7d0', 
                    borderRadius: 2 
                  }}
                >
                  <Typography variant="subtitle2" sx={{ color: '#166534', fontWeight: 'bold', mb: 0.5 }}>
                    สรุปผลคะแนนรวมที่ประเมิน
                  </Typography>
                  <Typography variant="h3" sx={{ fontWeight: '800', color: '#15803d', my: 1 }}>
                    {totalScore} <Typography component="span" variant="h5" color="textSecondary">/ {maxScore}</Typography>
                  </Typography>
                  <Chip 
                    label={`คิดเป็น ${percent}%`} 
                    color="success" 
                    size="small" 
                    sx={{ fontWeight: 'bold', fontSize: '0.85rem' }} 
                  />
                </Paper>

                {/* Section Breakdown */}
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, color: 'text.secondary' }}>
                  คะแนนแยกตามตอน / ด้าน:
                </Typography>
                <Paper variant="outlined" sx={{ mb: 2.5, borderRadius: 2, overflow: 'hidden' }}>
                  <List disablePadding>
                    {sectionScores.map((sec, idx) => (
                      <ListItem 
                        key={idx} 
                        divider={idx < sectionScores.length - 1} 
                        sx={{ py: 1.2, px: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary', pr: 2 }}>
                          {sec.title}
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'primary.main', whiteSpace: 'nowrap' }}>
                          {sec.score} / {sec.max} คะแนน
                        </Typography>
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              </Box>
            );
          })()}

          {/* Feedback Preview if any */}
          {feedback.trim() && (
            <Paper variant="outlined" sx={{ p: 2, mb: 2.5, bgcolor: '#fffbeb', borderColor: '#fef3c7', borderRadius: 2 }}>
              <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#b45309', display: 'block', mb: 0.5 }}>
                ข้อเสนอแนะเพิ่มเติม:
              </Typography>
              <Typography variant="body2" sx={{ color: '#78350f', whiteSpace: 'pre-wrap' }}>
                {feedback}
              </Typography>
            </Paper>
          )}

          {/* Warning Notice */}
          <Alert severity="warning" sx={{ borderRadius: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
              โปรดตรวจสอบความถูกต้องของคะแนนก่อนยืนยัน
            </Typography>
            <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
              เมื่อยืนยันและส่งผลคะแนนแล้ว ระบบจะทำการบันทึกข้อมูลเข้าสู่ฐานข้อมูลทันที และไม่สามารถแก้ไขได้ด้วยตนเอง
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button 
            onClick={() => setOpenConfirmEval(false)} 
            disabled={submittingEval}
            variant="outlined"
            color="inherit"
          >
            ย้อนกลับไปแก้ไขคะแนน
          </Button>
          <Button 
            onClick={handleFinalSubmitEval} 
            variant="contained" 
            color="success"
            disabled={submittingEval}
            startIcon={submittingEval ? <CircularProgress size={18} color="inherit" /> : <CheckCircleIcon />}
            sx={{ fontWeight: 'bold' }}
          >
            {submittingEval ? 'กำลังบันทึกคะแนน...' : 'ยืนยันและส่งคะแนน'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SupervisorStudents;
